import { useState, type FormEvent } from 'react'
import { Loader2, MailCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Logo } from '@/components/shared/Logo'
import { useAuth } from '@/lib/firebase/auth'
import { authErrorMessage } from '@/lib/firebase/auth-errors'
import { EmailLinkConfirm } from './EmailLinkConfirm'

type Mode = 'link' | 'password'

/**
 * Sign-in for the console. Google is the primary path (it is how the existing
 * admin.html signed in); for email, a passwordless sign-in link is the default
 * and email/password is kept as a fallback. Being an admin is decided by the
 * server, not here — this only proves identity.
 */
export function LoginPage() {
  const { signInWithGoogle, signInWithEmail, sendSignInLink, emailLinkStatus } = useAuth()
  const [mode, setMode] = useState<Mode>('link')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [linkSent, setLinkSent] = useState(false)

  const switchTo = (next: Mode) => {
    setMode(next)
    setError(null)
    setLinkSent(false)
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (mode === 'link') {
        await sendSignInLink(email)
        setLinkSent(true)
      } else {
        await signInWithEmail(email, password)
      }
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo className="size-14" />
          <h1 className="font-display text-xl font-semibold">VOYP Admin</h1>
          <p className="text-sm text-muted-foreground">Sign in with an admin account.</p>
        </div>

        {emailLinkStatus !== 'none' ? (
          <EmailLinkConfirm />
        ) : (
          <>
            <Button className="w-full" onClick={() => void signInWithGoogle()}>
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              or
              <span className="h-px flex-1 bg-border" />
            </div>

            {linkSent ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-lg border p-4 text-sm">
                  <MailCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                  <p className="text-muted-foreground">
                    We sent a sign-in link to{' '}
                    <span className="font-medium text-foreground">{email.trim()}</span>. Open it on
                    this device to finish signing in.
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="w-full" onClick={() => switchTo('link')}>
                  Back
                </Button>
              </div>
            ) : (
              <form className="space-y-3" onSubmit={onSubmit}>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                {mode === 'password' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" variant="outline" className="w-full" disabled={busy}>
                  {busy && <Loader2 className="size-4 animate-spin" />}
                  {mode === 'link' ? 'Email me a sign-in link' : 'Sign in'}
                </Button>
                <button
                  type="button"
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => switchTo(mode === 'link' ? 'password' : 'link')}
                >
                  {mode === 'link' ? 'Use a password instead' : 'Sign in without a password'}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  )
}
